import { TrendingDown, TrendingUp, CreditCard, Layers } from "lucide-react";
import { fmt, fmtDate, paymentSplitText, salesMixText } from "../lib/constants";
import { fmtRangeCountry, isSingleDay } from "../lib/dayView";
import { HINT, HowWeCountButton } from "./HowWeCount";
import { NotCountedChip } from "./NotCountedPanel";

const Stat = ({ label, value, target, testId, hint }) => (
  <div className="min-w-0" data-testid={`${testId}-row`} title={hint}>
    <div className="eyebrow whitespace-nowrap">{label}</div>
    <div data-testid={testId} className="num font-extrabold text-3xl tv:text-[45px] leading-none text-ink mt-1 tv:mt-[6px]">
      {fmt(value)}
    </div>
    {target ? (
      <div data-testid={`${testId}-target`} className="num text-[11px] tv:text-[14px] text-slate-400 mt-1 whitespace-nowrap">
        target {fmt(target)}
      </div>
    ) : null}
  </div>
);

// Monthly pace: expected venues by today = monthly target × elapsed days ÷ days in month (local time)
const pace = (targets, actual) => {
  if (!targets?.monthly_clients || !targets.month) return null;
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  if (ym !== targets.month) return null;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const expected = Math.round((targets.monthly_clients * now.getDate()) / daysInMonth);
  return { expected, diff: (actual || 0) - expected, day: now.getDate(), daysInMonth };
};

export const KPIPanel = ({ totals, newClients, targets, historyMode, countFrom, onHelp, range, onExitDay, notCounted, onNotCounted, payments, onPayments, salesMix, onSalesMix }) => {
  const total = totals?.total || 0;
  const wonPct = total ? Math.round(((totals?.won || 0) / total) * 100) : 0;
  const historyOn = historyMode === "all";
  const p = range ? null : pace(targets, newClients?.this_month);
  return (
    <section className="panel p-4 tv:p-[22px] fade-up" data-testid="kpi-panel">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 tv:gap-[22px]">
        <div className="min-w-0">
          <div className="eyebrow relative z-[1] flex items-center gap-2 flex-wrap">
            {range ? "Venues onboarded" : "Active venues"}
            {range ? (
              <>
                <span data-testid="kpi-day-mode" className="normal-case tracking-normal font-semibold rounded-full px-2 py-0.5 tv:px-[10px] tv:py-[2px] text-[10px] tv:text-[13px] bg-ink text-page">
                  {fmtRangeCountry(range)} · {isSingleDay(range) ? "day" : "range"} view
                </span>
                <button type="button" data-testid="kpi-day-mode-exit" onClick={onExitDay} className="normal-case tracking-normal font-semibold rounded-full px-2 py-0.5 tv:px-[10px] tv:py-[2px] text-[10px] tv:text-[13px] border border-panel-border text-slate-600 hover:text-ink hover:border-slate-400 transition-colors">
                  Back to live board
                </button>
              </>
            ) : (
              <span
                data-testid="kpi-view-mode"
                title={HINT.since}
                className={`normal-case tracking-normal font-semibold rounded-full px-2 py-0.5 tv:px-[10px] tv:py-[2px] text-[10px] tv:text-[13px] ${historyOn ? "bg-brand/10 text-brand" : "bg-slate-100 text-slate-600"}`}
              >
                {historyOn ? `all time · incl. history before ${fmtDate(countFrom)}` : `since ${fmtDate(countFrom)}`}
              </span>
            )}
            {onHelp && <HowWeCountButton onClick={onHelp} testId="kpi-help-button" size="w-3.5 h-3.5 tv:w-[18px] tv:h-[18px]" />}
          </div>
          <div className="flex items-end gap-3 tv:gap-[16px] flex-wrap">
            <div className="flex flex-col items-start gap-1.5 tv:gap-[8px]">
              <div data-testid="kpi-big-total" title={HINT.total} className="num font-black text-6xl tv:text-[86px] leading-[0.95] tracking-tight text-ink mt-1 tv:mt-[4px]">
                {fmt(total)}
              </div>
              {!range && notCounted && <NotCountedChip notCounted={notCounted} onClick={onNotCounted} />}
            </div>
            <div className="pb-1 tv:pb-[8px] flex flex-col items-start gap-1.5 tv:gap-[8px] min-w-0 max-w-full">
              <div title={HINT.productsSold}>
                <div className="eyebrow whitespace-nowrap">Products sold</div>
                <div className="flex items-baseline gap-2 tv:gap-[10px]">
                  <div data-testid="kpi-products-sold" className="num font-extrabold text-3xl tv:text-[40px] leading-none text-brand mt-1 tv:mt-[4px]">{fmt(totals?.products_sold)}</div>
                  {(totals?.won || 0) > 0 && (
                    <div data-testid="kpi-products-per-venue" className="num text-[11px] tv:text-[14px] text-slate-400 whitespace-nowrap" title="Products sold to new venues ÷ new venues (upsells and the Legacy base excluded)">
                      {(((totals?.products_sold || 0) - (totals?.upsell_products || 0)) / totals.won).toFixed(2)} per {historyOn ? "Won " : ""}venue
                    </div>
                  )}
                  {(totals?.upsell_products || 0) > 0 && (
                    <div data-testid="kpi-products-upsell" className="num text-[11px] tv:text-[14px] text-sky-700 font-semibold whitespace-nowrap" title={`${fmt(totals.upsell_products)} products sold since the cutoff to ${fmt(totals.upsell_venues)} venues that were already clients (upsell) – counted in Products sold and the product columns, not as new venues`}>
                      incl. {fmt(totals.upsell_products)} upsell
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 tv:gap-[8px] max-w-full min-w-0">
                {(payments?.total || 0) > 0 && (
                  <button
                    type="button"
                    data-testid="kpi-payments-split"
                    onClick={onPayments}
                    title={`${fmt(payments.total)} venues with Payments – by provider / channel: ${paymentSplitText(payments, { full: true })}. Click for the full breakdown with HubSpot links`}
                    className="inline-flex items-center gap-1.5 tv:gap-[6px] rounded-full border border-emerald-300 bg-emerald-50 text-emerald-900 px-2.5 py-1 tv:px-[10px] tv:py-[4px] text-xs tv:text-[13px] font-semibold whitespace-nowrap shadow-sm hover:border-emerald-500 transition-colors max-w-full"
                  >
                    <CreditCard className="w-3.5 h-3.5 tv:w-[15px] tv:h-[15px] text-emerald-700 shrink-0" />
                    <span>Payments <b className="num" data-testid="kpi-payments-total">{fmt(payments.total)}</b></span>
                    <span className="font-normal opacity-80 truncate" data-testid="kpi-payments-split-text">· {paymentSplitText(payments)}</span>
                  </button>
                )}
                {salesMixText(salesMix) && (
                  <button
                    type="button"
                    data-testid="kpi-sales-mix"
                    onClick={onSalesMix}
                    title={`Sales mix – venues with at least one line item of that kind: ${salesMixText(salesMix)}. Payments fees are not part of the mix. Click for the breakdown by product and country and the line items behind each kind`}
                    className="inline-flex items-center gap-1.5 tv:gap-[6px] rounded-full border border-slate-300 bg-slate-50 text-slate-800 px-2.5 py-1 tv:px-[10px] tv:py-[4px] text-xs tv:text-[13px] font-semibold whitespace-nowrap shadow-sm hover:border-slate-500 transition-colors max-w-full min-w-0"
                  >
                    <Layers className="w-3.5 h-3.5 tv:w-[15px] tv:h-[15px] text-slate-600 shrink-0" />
                    <span className="num truncate min-w-0 sm:hidden">{salesMixText(salesMix, { short: true })}</span>
                    <span className="num truncate min-w-0 hidden sm:inline" data-testid="kpi-sales-mix-text">{salesMixText(salesMix)}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-5 tv:gap-[22px] tv:gap-[28px] sm:text-right shrink-0">
          <Stat label="New today" value={newClients?.today} target={targets?.daily_clients} testId="kpi-new-today" hint={HINT.today} />
          <Stat label="Last 7 days" value={newClients?.last_7_days} testId="kpi-new-7days" hint={HINT.week} />
          <Stat label="This month" value={newClients?.this_month} target={targets?.monthly_clients} testId="kpi-new-month" hint={HINT.month} />
        </div>
      </div>
      <div className="mt-4 tv:mt-[19px]" data-testid="kpi-split-bar">
        <div className="h-2 tv:h-[11px] rounded-full bg-slate-200 overflow-hidden">
          <div className="h-full bg-brand rounded-full transition-[width] duration-700" style={{ width: `${wonPct}%` }} />
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 tv:gap-x-[26px] mt-2 tv:mt-[10px] text-xs tv:text-[16px] text-slate-600">
          <span className="inline-flex items-center gap-1.5" title={HINT.won}>
            <span className="w-2.5 h-2.5 tv:w-[12px] tv:h-[12px] rounded-[3px] bg-brand" />
            Won venues <b data-testid="kpi-won-count" className="num text-ink">{fmt(totals?.won)}</b>
          </span>
          <span className="inline-flex items-center gap-1.5" title={HINT.legacy}>
            <span className="w-2.5 h-2.5 tv:w-[12px] tv:h-[12px] rounded-[3px] bg-slate-300" />
            Legacy base <b data-testid="kpi-legacy-count" className="num text-ink">{fmt(totals?.legacy)}</b>
          </span>
          <span title={HINT.pipeline}>
            Pipeline <b data-testid="kpi-onboarding-count" className="num text-ink">{fmt(totals?.onboarding)}</b>
          </span>
          {totals?.partner ? (
            <span className="inline-flex items-center gap-1.5" title={HINT.partner}>
              <span className="w-2.5 h-2.5 tv:w-[12px] tv:h-[12px] rounded-[3px] bg-amber-400" />
              Via partners <b data-testid="kpi-partner-count" className="num text-ink">{fmt(totals.partner)}</b>
            </span>
          ) : null}
          {p && (
            <span
              data-testid="kpi-pace"
              title={HINT.pace}
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 tv:px-[10px] tv:py-[3px] font-semibold whitespace-nowrap ${p.diff >= 0 ? "bg-live/10 text-live" : "bg-brand/10 text-brand"}`}
            >
              {p.diff >= 0 ? <TrendingUp className="w-3.5 h-3.5 tv:w-[16px] tv:h-[16px]" /> : <TrendingDown className="w-3.5 h-3.5 tv:w-[16px] tv:h-[16px]" />}
              <span className="num">{p.diff >= 0 ? "+" : "−"}{fmt(Math.abs(p.diff))}</span> {p.diff >= 0 ? "ahead of" : "behind"} pace
              <span className="hidden sm:inline num font-normal opacity-80">· {fmt(p.expected)} expected by day {p.day}/{p.daysInMonth}</span>
            </span>
          )}
          <span className="sm:ml-auto text-slate-500" title={HINT.paymentsMonth}>
            Payment this month <b data-testid="kpi-new-month-payments" className="num text-ink">{fmt(newClients?.this_month_payments)}</b>
            {targets?.monthly_payments ? <span className="num text-slate-400"> / {fmt(targets.monthly_payments)}</span> : null}
            {newClients?.this_month_payments_existing ? (
              <span data-testid="kpi-new-month-payments-existing" className="num text-slate-400 text-[0.85em]"> · incl. {fmt(newClients.this_month_payments_existing)} existing</span>
            ) : null}
          </span>
        </div>
      </div>
    </section>
  );
};
